// @ts-check
// Célula inline da página de registro (doc 10, 4.2 e 4.3) e célula de título (doc 10, seção 3).
import { html } from 'htm/preact';
import { isFieldLockedByStage, resolveStageTransition } from '../object-record/stage-transition.js';
import { useRef, useState } from 'preact/hooks';
import { FieldDisplay, RatingStars, ratingToNumber } from '../object-record/field-display.js';
import { FieldInput, INPUT_ONLY_FIELD_TYPES, getInputOnlyPatch, isFieldEditableInline } from '../object-record/field-input.js';
import { updateRecordOptimistically } from '../../data/record-store.js';
import { FIELD_TYPE, formatFullName, getFieldIcon, isFieldReadOnly } from '../../lib/field-metadata.js';
import { Icon } from '../../ui/icon.js';
import { useClickOutside } from '../../ui/hooks.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';

/**
 * @typedef {import('../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {import('../../lib/field-metadata.js').FieldMetadata} FieldMetadata
 */

/**
 * @param {{ objectMetadata: ObjectMetadata, field: FieldMetadata, record: any, isRecordReadOnly?: boolean }} props
 */
export function InlineCell({ objectMetadata, field, record, isRecordReadOnly = false }) {
  const [isEditing, setIsEditing] = useState(false);
  const isLockedByStage = isFieldLockedByStage(objectMetadata, field, record);
  const isReadOnly = isRecordReadOnly || isFieldReadOnly(field) || !isFieldEditableInline(field) || isLockedByStage;
  const commit = async (/** @type {import('../object-record/field-input.js').FieldPatch} */ requestedPatch) => {
    const patch = await resolveStageTransition(objectMetadata, record, requestedPatch);
    if (!patch) return;
    updateRecordOptimistically(objectMetadata, record.id, patch.apiPatch, patch.localPatch).catch(() => {});
  };
  const open = () => {
    if (isReadOnly) return;
    if (INPUT_ONLY_FIELD_TYPES.has(field.type)) {
      if (field.type === FIELD_TYPE.BOOLEAN) commit(getInputOnlyPatch(field, record));
      return;
    }
    setIsEditing(true);
  };
  return html`<div class="inline-cell" data-readonly=${isReadOnly ? 'true' : undefined}>
    <div class="inline-cell-label" title=${translateMetadataLabel(field.label)}>
      <${Icon} name=${getFieldIcon(field)} size=${16} stroke=${1.6} />
      <span class="inline-cell-label-text" title=${translateMetadataLabel(field.label)}>${translateMetadataLabel(field.label)}</span>
    </div>
    <div class="inline-cell-value" data-editing=${isEditing ? 'true' : undefined} onClick=${(event) => {
      if (/** @type {HTMLElement} */ (event.target).closest('a, .rating-star')) return;
      open();
    }}>
      <div class="inline-cell-display">
        ${field.type === FIELD_TYPE.RATING && !isReadOnly
          ? html`<${RatingStars} value=${ratingToNumber(record[field.name])} onSelect=${(rating) => commit(getInputOnlyPatch(field, record, rating))} />`
          : html`<${FieldDisplay} field=${field} record=${record} emptyLabel=${translateMetadataLabel(field.label)} />`}
      </div>
      ${isEditing
        ? html`<${FieldInput}
            field=${field}
            record=${record}
            onCommit=${(patch) => {
              setIsEditing(false);
              commit(patch);
            }}
            onCancel=${() => setIsEditing(false)}
          />`
        : null}
    </div>
  </div>`;
}

/**
 * Título editável (TEXT ou FULL_NAME). Enter, Tab e clique fora salvam; Escape descarta.
 * @param {{ objectMetadata: ObjectMetadata, field: FieldMetadata | undefined, record: any, size?: 'xs' | 'md', isReadOnly?: boolean }} props
 */
export function RecordTitleCell({ objectMetadata, field, record, size = 'md', isReadOnly = false }) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(/** @type {any} */ (null));
  const containerRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const isFullName = field?.type === FIELD_TYPE.FULL_NAME;
  const value = field ? record?.[field.name] : null;
  const displayText = isFullName ? formatFullName(value) : value ?? '';

  const persist = () => {
    if (!field) return;
    setIsEditing(false);
    const nextValue = isFullName
      ? { firstName: (draft?.firstName ?? '').trim(), lastName: (draft?.lastName ?? '').trim() }
      : String(draft ?? '').trim() || null;
    if (JSON.stringify(nextValue) === JSON.stringify(isFullName ? { firstName: value?.firstName ?? '', lastName: value?.lastName ?? '' } : value ?? null)) return;
    updateRecordOptimistically(objectMetadata, record.id, { [field.name]: nextValue }).catch(() => {});
  };
  useClickOutside(containerRef, persist, isEditing);

  /** @param {KeyboardEvent} event */
  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || (event.key === 'Tab' && !(isFullName && !event.shiftKey && /** @type {HTMLInputElement} */ (event.target).dataset.part === 'first'))) {
      event.preventDefault();
      persist();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setIsEditing(false);
    }
  };

  if (!field) return html`<span class="record-title" data-size=${size}>${t('record.untitled')}</span>`;

  if (!isEditing) {
    return html`<div
      class="record-title"
      data-size=${size}
      data-readonly=${isReadOnly ? 'true' : undefined}
      title=${displayText}
      onClick=${() => {
        if (isReadOnly) return;
        setDraft(isFullName ? { firstName: value?.firstName ?? '', lastName: value?.lastName ?? '' } : value ?? '');
        setIsEditing(true);
      }}
    >
      <span class=${displayText ? '' : 'record-title-placeholder'}>${displayText || translateMetadataLabel(field.label)}</span>
    </div>`;
  }

  const focusFirst = (/** @type {HTMLInputElement | null} */ element) => {
    if (element && !element.dataset.focused) {
      element.dataset.focused = 'true';
      element.focus();
      element.select();
    }
  };

  return html`<div class="record-title record-title-editing" data-size=${size} ref=${containerRef}>
    ${isFullName
      ? html`
          <input class="record-title-input" data-part="first" value=${draft?.firstName ?? ''} placeholder=${t('fieldInput.firstName')} ref=${focusFirst}
            onInput=${(event) => setDraft({ ...draft, firstName: event.currentTarget.value })} onKeyDown=${handleKeyDown} />
          <input class="record-title-input" data-part="last" value=${draft?.lastName ?? ''} placeholder=${t('fieldInput.lastName')}
            onInput=${(event) => setDraft({ ...draft, lastName: event.currentTarget.value })} onKeyDown=${handleKeyDown} />
        `
      : html`<input class="record-title-input" value=${draft ?? ''} placeholder=${translateMetadataLabel(field.label)} ref=${focusFirst}
          onInput=${(event) => setDraft(event.currentTarget.value)} onKeyDown=${handleKeyDown} />`}
  </div>`;
}
